/* eslint-disable react/no-unescaped-entities --
 * v77.99.77: Idiomatic React patterns (effect-driven state hydration,
 * English text with apostrophes/quotes in JSX). These experimental
 * rules are not shipped by eslint-plugin-react-hooks 5.2.0 and
 * mis-flag intentional patterns. `react/no-unescaped-entities`
 * similarly flags safe English text.
 */
// v77.99.23 — Calendar Slice 4 refactor.
//
// NewEventDialog — the "Add event" modal accessed from the page header.
// Extracted from CalendarPage.jsx (originally ~100 inline lines).
//
// Symmetrical with DayDetailDialog + EditOccurrenceDialog: this is a
// pure presentational/state-coupled extraction. All form state lives
// on the parent page (`form`, `setForm`), as does the open flag and
// every action (`createEvent`, `tryCloseNewEvent`, `openCreate`,
// `isNewEventDirty`). We never own copies of those.
//
// Anatomy:
//   1. Trigger button ("Add event") in the page header.
//   2. Modal with the create form: title, type+parent grid, start+end
//      grid, child picker, recurrence fields, reminder fields,
//      notes, shared/private toggle, Save button.
//
// Why a separate component when it could have stayed inline?
//   • Symmetry with the other dialog extractions for slice-3 maintainability.
//   • Centralises the form contract so future fields (e.g. location,
//     reminders v2) only need to touch one file.
//   • Lets the CalendarPage stay below the ~1500-line readability
//     threshold the team has been tracking.

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Eye, EyeOff, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { ACTIVITY_PRESETS, applyPresetToForm } from "@/lib/activityPresets";
import { UpgradePrompt } from "@/components/UpgradePrompt";
import { InfoTooltip } from "@/components/InfoTooltip";
import {
  RecurrenceFields,
  ReminderFields,
} from "@/components/calendar/ScheduleControls";

const TYPE_LABEL = {
  // v77.99.43 — Renamed from plain "Custody" to make it obvious this
  // is a *one-off* deviation, not the recurring parenting schedule.
  // Recurring arrangements live in Calendar → "Set up parenting
  // schedule"; this option here is for swaps, holiday moves, etc.
  custody: "Custody change (one-off)",
  pickup: "Pick-up",
  dropoff: "Drop-off",
  school: "School",
  extracurricular: "Extracurricular",
  sports: "Sports",
  holiday: "Holiday",
  call: "Phone call",
  video_call: "Video call",
  other: "Other",
};

export function NewEventDialog({
  open,
  form,
  setForm,
  childList,
  eligibleParentsFor,
  toggleChildInForm,
  openCreate,
  tryCloseNewEvent,
  isNewEventDirty,
  createEvent,
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (v) openCreate();
        else tryCloseNewEvent();
      }}
    >
      <DialogTrigger asChild>
        <Button
          className="bg-[#1F9FA4] hover:bg-[#178085] rounded-sm"
          data-testid="add-event-button"
        >
          <Plus className="w-4 h-4 mr-2" /> Add event
        </Button>
      </DialogTrigger>
      <DialogContent
        preventOutsideClose
        className="bg-white border-[#D9E1E3] max-h-[90vh] overflow-y-auto"
        onEscapeKeyDown={(e) => {
          if (isNewEventDirty()) {
            e.preventDefault();
            tryCloseNewEvent();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="font-heading">New event</DialogTitle>
        </DialogHeader>
        <UpgradePrompt
          feature="create calendar events"
          testid="calendar-upgrade-prompt"
        />
        <form onSubmit={createEvent} className="space-y-3">
          {/* v77.99.31 — Activity preset quick-fill. Optional one-tap
              starter for common kids' activities. Choosing a preset
              fills Title + Type + a sensible Recurrence + (if the
              user already entered a start time) an End derived from
              the preset's typical duration. Every field stays fully
              editable after the fill so the preset is a STARTER, not
              a constraint. We deliberately render this OUTSIDE the
              normal field grid to signal "this is a shortcut, not a
              required field". */}
          <div>
            <Label className="inline-flex items-center gap-1.5 text-[#116B6E]">
              <Sparkles className="w-3.5 h-3.5" strokeWidth={1.8} aria-hidden="true" />
              Quick-fill from an activity (optional)
            </Label>
            <Select
              value=""
              onValueChange={(v) => {
                const preset = ACTIVITY_PRESETS.find((p) => p.id === v);
                if (!preset) return;
                setForm(applyPresetToForm(form, preset));
                toast.success(
                  `Filled from "${preset.label}" — pick a start time and edit anything before saving.`,
                );
              }}
            >
              <SelectTrigger
                className="border-[#A6D7D9] bg-[#F2FAFA] text-[#116B6E]"
                data-testid="event-activity-preset-select"
              >
                <SelectValue placeholder="Football, swimming, dance, music…" />
              </SelectTrigger>
              <SelectContent>
                {ACTIVITY_PRESETS.map((p) => (
                  <SelectItem
                    key={p.id}
                    value={p.id}
                    data-testid={`event-activity-preset-${p.id}`}
                  >
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Title</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
              data-testid="event-title-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="inline-flex items-center gap-1.5">
                Type
                <InfoTooltip
                  text="Use 'Custody change' for one-off swaps or temporary deviations from your recurring Parenting Schedule. Set up the recurring schedule from the Calendar's 'Set up parenting schedule' button."
                  testid="help-tooltip-event-type"
                />
              </Label>
              <Select
                value={form.type}
                onValueChange={(v) => setForm({ ...form, type: v })}
              >
                <SelectTrigger data-testid="event-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABEL).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* v77.99.43 — Inline reminder so the user doesn't
                  reach for "Custody change" hoping it'll become a
                  recurring schedule. Shown only when the user has
                  actually picked the custody option. */}
              {form.type === "custody" && (
                <p className="text-[11px] leading-relaxed text-[#8F6B1F] mt-1.5 px-2 py-1.5 border border-[#E6CFA2] bg-[#FFF6E1] rounded-sm" data-testid="event-type-custody-hint">
                  A <span className="font-medium">one-off</span> custody change for this single date. For your recurring custody arrangement, close this and tap <span className="font-medium">"Set up parenting schedule"</span> on the calendar.
                </p>
              )}
            </div>
            <div>
              <Label>Parent</Label>
              <Select
                value={form.parent_id}
                onValueChange={(v) => setForm({ ...form, parent_id: v })}
              >
                <SelectTrigger data-testid="event-parent-select">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                {/* v77.10 — Restrict to parents linked to the selected
                    child(ren). When the user hasn't picked a child
                    yet, fall back to the full household list. */}
                <SelectContent>
                  {eligibleParentsFor(form.child_ids).map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start</Label>
              <Input
                type="datetime-local"
                value={form.start}
                onChange={(e) => setForm({ ...form, start: e.target.value })}
                required
                data-testid="event-start-input"
              />
            </div>
            <div>
              <Label>
                End{" "}
                <span className="text-[#6F828A] font-normal">
                  {form.freq && form.freq !== "none" ? "(optional)" : ""}
                </span>
              </Label>
              <Input
                type="datetime-local"
                value={form.end}
                onChange={(e) => setForm({ ...form, end: e.target.value })}
                required={!form.freq || form.freq === "none"}
                data-testid="event-end-input"
              />
            </div>
          </div>

          {childList.length > 0 && (
            <div>
              <Label className="flex items-center justify-between">
                <span>Children</span>
                <span className="text-[10px] uppercase tracking-[0.1em] text-[#825A41] font-normal">
                  Required
                </span>
              </Label>
              <div
                className="flex flex-wrap gap-2 mt-1"
                data-testid="event-children-picker"
              >
                {childList.map((c) => {
                  const active = form.child_ids.includes(c.id);
                  return (
                    <button
                      type="button"
                      key={c.id}
                      onClick={() =>
                        toggleChildInForm(
                          c.id,
                          (list) => setForm({ ...form, child_ids: list }),
                          form.child_ids,
                        )
                      }
                      className={`inline-flex items-center gap-1.5 px-2 py-1 border rounded-sm text-xs transition-colors ${active ? "bg-[#EEF3F4] border-[#2E3A3F] text-[#2E3A3F]" : "bg-white border-[#D9E1E3] text-[#6F828A] hover:bg-[#F5F7F8]"}`}
                      data-testid={`event-child-${c.id}`}
                    >
                      <span
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: c.color || "#1F9FA4" }}
                      ></span>
                      {c.name}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-[#6F828A] mt-1">
                Pick one or more. The event will only be visible under those
                filters.
              </p>
            </div>
          )}

          <RecurrenceFields form={form} setForm={setForm} />
          <ReminderFields form={form} setForm={setForm} />

          <div>
            <Label>Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="event-notes-input"
            />
          </div>

          <div className="flex items-center justify-between border border-[#D9E1E3] rounded-sm p-3">
            <div>
              <div className="text-sm font-medium inline-flex items-center gap-1.5">
                {form.is_shared ? (
                  <Eye className="w-3.5 h-3.5 text-[#2E3E48]" />
                ) : (
                  <EyeOff className="w-3.5 h-3.5 text-[#825A41]" />
                )}
                Shared with co-parent(s)
              </div>
              <div className="text-xs text-[#6F828A]">
                {form.is_shared
                  ? "Visible to your co-parent. Recommended for transparency."
                  : "Private — only you can see this event."}
              </div>
            </div>
            <Switch
              checked={form.is_shared}
              onCheckedChange={(v) => setForm({ ...form, is_shared: v })}
              data-testid="event-share-switch"
            />
          </div>

          <DialogFooter>
            <Button
              type="submit"
              className="bg-[#1F9FA4] hover:bg-[#178085]"
              data-testid="event-save-button"
            >
              Save event
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default NewEventDialog;
